package com.evoapps.evoshift.ui.voice

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.location.rememberLocationReader
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.user.ClockNote
import com.evoapps.evoshift.ui.user.LocationNoticeDialog
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.VoiceActionViewModel
import com.evoapps.evoshift.voice.LocalSpeech
import com.evoapps.evoshift.voice.VoiceAction
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.helpAction
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.confirm
import evoshift.composeapp.generated.resources.voice_title_clock_in
import evoshift.composeapp.generated.resources.voice_title_clock_out
import evoshift.composeapp.generated.resources.voice_title_end_break
import evoshift.composeapp.generated.resources.voice_title_next_shift
import evoshift.composeapp.generated.resources.voice_title_start_break
import evoshift.composeapp.generated.resources.voice_title_work_tomorrow
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

const val VOICE_MESSAGE_TAG = "voice_message"
const val VOICE_QUESTION_TAG = "voice_question"

/** SH-150: the title of [action]'s screen, as the assistant's shortcut is called. */
fun VoiceAction.titleRes(): StringResource = when (this) {
    VoiceAction.NEXT_SHIFT -> Res.string.voice_title_next_shift
    VoiceAction.WORK_TOMORROW -> Res.string.voice_title_work_tomorrow
    VoiceAction.CLOCK_IN -> Res.string.voice_title_clock_in
    VoiceAction.CLOCK_OUT -> Res.string.voice_title_clock_out
    VoiceAction.START_BREAK -> Res.string.voice_title_start_break
    VoiceAction.END_BREAK -> Res.string.voice_title_end_break
}

/**
 * SH-150: what a voice assistant opened (Google Assistant / Gemini app action, an Android shortcut,
 * or Siri when the workplace checks the location): the answer about the person's own shifts, or
 * the clock action's question with **Confirm** / **Cancel**. Every text is also read out loud
 * ([LocalSpeech]) so it works hands-free; nothing is clocked without the tap on Confirm.
 */
@Composable
fun VoiceActionScreen(viewModel: VoiceActionViewModel, onClose: () -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val readLocation = rememberLocationReader()
    val speech = LocalSpeech.current
    val question = state.question?.asString()
    val message = state.message?.asString()
    val spoken = question ?: message
    LaunchedEffect(spoken) { spoken?.let(speech::speak) }

    if (state.locationNotice) LocationNoticeDialog(viewModel::onLocationNoticeAnswered)

    Scaffold(
        topBar = {
            EvoTopBar(
                title = stringResource(state.action.titleRes()),
                onBack = onClose,
                actions = listOf(helpAction(HelpPage.VOICE))
            )
        }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize(), contentAlignment = Alignment.Center) {
            if (state.loading) {
                CircularProgressIndicator()
                return@Box
            }
            Column(
                Modifier.contentWidth(FormMaxWidth).verticalScroll(rememberScrollState()).padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                if (question != null) {
                    Text(
                        question,
                        style = MaterialTheme.typography.headlineSmall,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.testTag(VOICE_QUESTION_TAG)
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        OutlinedButton(onClick = viewModel::cancel, enabled = !state.busy) {
                            Text(stringResource(Res.string.cancel))
                        }
                        Button(onClick = { viewModel.confirm(readLocation) }, enabled = !state.busy) {
                            if (state.busy) CircularProgressIndicator(Modifier.padding(2.dp), strokeWidth = 2.dp)
                            else Text(stringResource(Res.string.confirm))
                        }
                    }
                } else if (message != null) {
                    Text(
                        message,
                        style = MaterialTheme.typography.headlineSmall,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth().testTag(VOICE_MESSAGE_TAG)
                    )
                    ClockNote(state.note, onDismiss = {})
                }
            }
        }
    }
}
